import { Chip, Stack, TextField } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { Box } from "@mui/system";
import { FC, useEffect, useMemo, useState } from "react";
import { TAG_COLOR } from "helpers/constants";
import { Tag } from "types/tag";
import {
  useGetTagsByTypeQuery,
  useGetTagsQuery,
} from "graphql/__generated__/hooks";
import { useAuthUser } from "hooks/useAuthUser";
import { filterAdminOnlyTags } from "utils/adminOnlyContent";
import { ROLES } from "types/user";
import { isAdminOnlyContentTag } from "types/tag";

type TagManagerProps = {
  tags: Tag[];
  onAdd?: (tag: Tag) => void;
  onDelete?: (tag: Tag) => void;
  isEditing: boolean;
  tagType?: string;
  placeholder?: string;
  /** When set, only these tags appear in the autocomplete (already filtered by caller). */
  availableTags?: Tag[];
  /** Allow typing a new tag name (freeSolo). New names are passed to onAdd without tag_id. */
  allowCreate?: boolean;
};

export const TagManager: FC<TagManagerProps> = ({
  tags,
  onAdd,
  onDelete,
  isEditing,
  tagType,
  placeholder = "Select Tag",
  availableTags,
  allowCreate = false,
}) => {
  const [allTags, setAllTags] = useState([] as Tag[]);
  const [value, setValue] = useState(null as Tag | null);
  const { user } = useAuthUser();
  const { data: allTagsData } = useGetTagsQuery({
    skip: Boolean(tagType) || availableTags !== undefined,
  });
  const { data: typedTagsData } = useGetTagsByTypeQuery({
    variables: { type: tagType || "" },
    skip: !tagType || availableTags !== undefined,
  });

  useEffect(() => {
    if (availableTags !== undefined) {
      setAllTags(filterAdminOnlyTags(availableTags, user?.role));
      return;
    }
    if (tagType) {
      setAllTags(
        filterAdminOnlyTags((typedTagsData?.tag as Tag[]) || [], user?.role),
      );
      return;
    }
    setAllTags(
      filterAdminOnlyTags((allTagsData?.tag as Tag[]) || [], user?.role),
    );
  }, [allTagsData, typedTagsData, tagType, availableTags, user?.role]);

  const filteredOptions = useMemo(() => {
    return allTags.filter((option) => {
      return !tags.some(
        (tag) => tag.tag_id === option.tag_id || tag.name === option.name,
      );
    });
  }, [tags, allTags]);

  const handleDelete = (tagToRemove: Tag) => {
    // Non-admins cannot remove the system admin-only tag even if somehow present.
    if (
      user?.role !== ROLES.ADMIN &&
      isAdminOnlyContentTag(tagToRemove)
    ) {
      return;
    }
    if (onDelete) onDelete(tagToRemove);
  };

  return (
    <Stack spacing={2}>
      {isEditing && onAdd && (
        <Autocomplete
          key={tags.length}
          fullWidth
          value={value}
          onChange={(_, newValue) => {
            if (newValue === null) {
              setValue(null);
              return;
            }
            setValue(null);
            if (typeof newValue === "string") {
              const name = newValue.trim();
              if (!name) return;
              if (
                user?.role !== ROLES.ADMIN &&
                isAdminOnlyContentTag({ name })
              ) {
                return;
              }
              onAdd({ name, type: tagType || "" });
              return;
            }
            onAdd(newValue as Tag);
          }}
          selectOnFocus
          clearOnBlur
          handleHomeEndKeys
          id="tag-manager"
          options={filteredOptions}
          getOptionLabel={(option) => {
            if (typeof option === "string") return option;
            return (option as Tag)?.name;
          }}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            return (
              <li key={key} {...optionProps}>
                {option.name}
              </li>
            );
          }}
          freeSolo={!tagType || allowCreate}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={placeholder}
              variant="standard"
            />
          )}
        />
      )}
      <Box mt={3} sx={{ flexGrow: 1 }}>
        {filterAdminOnlyTags(tags, user?.role).map((data, index) => {
          return (
            <Chip
              sx={{ marginLeft: 1, marginBottom: 1 }}
              key={data.tag_id || index}
              label={data.name}
              color={TAG_COLOR(data.type)}
              onDelete={isEditing ? () => handleDelete(data) : undefined}
            />
          );
        })}
      </Box>
    </Stack>
  );
};
